import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { CourseDesignChat } from "@/components/CourseDesignChat";
import CourseCreationChat from "@/components/CourseCreationChat";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";
import { useTutorial } from "@/hooks/useTutorial";
import { HelpCircle, CreditCard, LogOut, Home, Plus, ArrowUp } from "lucide-react";

const DEFAULT_SUGGESTIONS = [
  "The science of sleep",
  "How does inflation work?",
  "How do mountains form?",
  "What are CRISPR gene edits?",
  "Basics of Macroeconomics",
  "Project Management",
];

const LogoIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8">
    <circle cx="16" cy="16" r="15" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 16 C10 11, 16 8, 22 12 C22 17, 16 24, 10 20 C10 18, 12 17, 16 16 C14 14.5, 11 14, 10 16Z"
      fill="currentColor"
      opacity={0.8}
    />
    <circle cx="20" cy="12" r="2" fill="currentColor" />
  </svg>
);

export default function LearnAboutPage() {
  const [input, setInput] = useState("");
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState("");
  const navigate = useNavigate();
  const { session } = useAuth();
  const { userType, isLoading: typeLoading } = useUserType(session?.user?.id);
  const { restartTutorial } = useTutorial();
  const isAdminOrDesigner = userType === "admin" || userType === "designer";

  const { data: suggestionsData } = useQuery({
    queryKey: ["learn-about-suggestions"],
    queryFn: async () => {
      const { data: workspaces } = await supabase
        .from("workspaces")
        .select("id, name")
        .order("created_at", { ascending: false });
      if (!workspaces?.length) return { suggestions: [] };
      const workspaceIds = workspaces.map((w) => w.id);
      const { data: courses } = await supabase
        .from("learning_projects")
        .select("id, title, workspace_id")
        .in("workspace_id", workspaceIds)
        .order("created_at", { ascending: false });
      const byWorkspace = new Map<string, { name: string; titles: string[] }>();
      workspaces.forEach((w) => byWorkspace.set(w.id, { name: w.name ?? "Workspace", titles: [] }));
      courses?.forEach((c) => {
        const ws = byWorkspace.get(c.workspace_id);
        if (ws && c.title) ws.titles.push(c.title);
      });
      const suggestions: string[] = [];
      byWorkspace.forEach((ws) => {
        ws.titles.forEach((t) => suggestions.push(t));
      });
      return { suggestions: [...new Set(suggestions)] };
    },
  });

  const suggestions = (suggestionsData?.suggestions?.length ? suggestionsData.suggestions : DEFAULT_SUGGESTIONS) as string[];

  const handleSubmit = (topic: string) => {
    const t = topic.trim();
    if (!t) return;
    setSelectedTopic(t);
    setShowChatDialog(true);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#faf9f7]">
      {/* Soft gradient overlay - cream to warm white */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#faf9f7] via-[#f5f3ef] to-[#f0ede8] -z-10" />

      <header className="border-b border-[#e8e5e0] bg-[#faf9f7]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex flex-row justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-[#1a1a1a] font-semibold">
            <LogoIcon />
            <span className="text-lg">Distilled Intuition</span>
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" className="text-[#4a4a4a] hover:text-foreground" asChild>
              <Link to="/dashboard" className="gap-1.5 sm:gap-2">
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">My Page</span>
              </Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={restartTutorial} className="shrink-0 text-[#4a4a4a] hover:text-foreground">
              <HelpCircle className="mr-1.5 h-4 w-4" />
              <span className="hidden sm:inline">Tutorial</span>
            </Button>
            <Button variant="ghost" size="sm" asChild className="text-[#4a4a4a] hover:text-foreground">
              <Link to="/request-demo" className="gap-1.5">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Request demo</span>
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={handleSignOut} className="shrink-0 border-[#ddd] text-[#4a4a4a]">
              <LogOut className="mr-1.5 h-4 w-4" />
              Sign Out
            </Button>
          </nav>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-2xl mx-auto">
          {/* Centered hero - Oboe style */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e8e5e0]/60 mb-6">
              <LogoIcon />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-[#1a1a1a] tracking-tight leading-tight">
              What do you want to learn about?
            </h1>
            <p className="text-[#6b6b6b] text-base sm:text-lg mt-3 max-w-md mx-auto">
              Enriched, personalized, and interactive courses. Get started for free.
            </p>
          </div>

          {/* Single large input - Oboe style with + and submit arrow */}
          <div className="relative rounded-2xl border border-[#ddd] bg-white shadow-sm shadow-[#e8e5e0]/50 focus-within:border-[#1C3829]/40 focus-within:ring-2 focus-within:ring-[#1C3829]/10 focus-within:ring-offset-2 transition-all">
            <div className="flex items-center gap-2 px-4 py-1 min-h-[56px] sm:min-h-[64px]">
              <button
                type="button"
                className="p-2 rounded-full hover:bg-[#f0ede8] text-[#6b6b6b] hover:text-[#1a1a1a] transition-colors shrink-0"
                aria-label="Add"
              >
                <Plus className="h-5 w-5" />
              </button>
              <input
                type="text"
                placeholder="I want to learn about..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(input)}
                className="flex-1 min-w-0 bg-transparent text-[#1a1a1a] placeholder:text-[#9a9a9a] focus:outline-none text-base sm:text-lg py-2"
              />
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-[#9a9a9a] hidden sm:inline">Configure</span>
                <button
                  type="button"
                  onClick={() => handleSubmit(input)}
                  disabled={!input.trim()}
                  className="h-9 w-9 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center hover:bg-[#333] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                  aria-label="Submit"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Suggested topics - list with arrows */}
          {suggestions.length > 0 && (
            <div className="mt-8">
              <p className="text-xs font-medium text-[#9a9a9a] uppercase tracking-wide mb-3 text-left">
                {suggestionsData?.suggestions?.length ? "From your workspaces" : "Suggestions"}
              </p>
              <ul className="space-y-1">
                {suggestions.slice(0, 6).map((label, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => handleSubmit(label)}
                      className="flex items-center gap-3 w-full text-left py-2.5 px-1 rounded-lg text-[#1a1a1a] hover:bg-[#e8e5e0]/50 transition-colors group"
                    >
                      <ArrowUp className="h-4 w-4 rotate-[-90deg] text-[#9a9a9a] group-hover:text-[#1C3829] shrink-0" />
                      <span className="text-sm sm:text-base">{label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>

      {showChatDialog && selectedTopic && !typeLoading && isAdminOrDesigner && (
        <CourseCreationChat
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setShowChatDialog(false);
              setSelectedTopic("");
              setInput("");
            }
          }}
          onSuccess={() => {
            setShowChatDialog(false);
            setSelectedTopic("");
            setInput("");
            navigate("/dashboard");
          }}
          initialTopic={selectedTopic}
        />
      )}
      {showChatDialog && selectedTopic && (!isAdminOrDesigner || typeLoading) && (
        <CourseDesignChat
          initialTopic={selectedTopic}
          onClose={() => {
            setShowChatDialog(false);
            setSelectedTopic("");
            setInput("");
          }}
        />
      )}
    </div>
  );
}
